/**
 * Gen2 interim hierarchy helpers.
 *
 * Gen2 Location has no districtId yet, so nested locations store their parent
 * district id in description until the real District model is restored.
 * Format: `__eifDistrictParent:<districtId>` or
 * `__eifDistrictParent:<districtId>\n<user description>`
 *
 * Also reads residual Gen1 `districtId` when present on the record at runtime.
 */
export const EIF_DISTRICT_PARENT_PREFIX = '__eifDistrictParent:';

type WithDescription = {
    description?: string | null;
    districtId?: string | null;
};

export function getInterimDistrictParentId(location: WithDescription): string | null {
    const description = location.description || '';
    if (description.startsWith(EIF_DISTRICT_PARENT_PREFIX)) {
        const rest = description.slice(EIF_DISTRICT_PARENT_PREFIX.length);
        const newline = rest.indexOf('\n');
        const fromDescription = (newline === -1 ? rest : rest.slice(0, newline)).trim();
        if (fromDescription) return fromDescription;
    }
    // Residual Gen1 field (may still exist on migrated records even if omitted from Gen2 types)
    const residual = location.districtId?.trim();
    return residual || null;
}

export function getInterimLocationDescription(location: WithDescription): string {
    const description = location.description || '';
    if (!description.startsWith(EIF_DISTRICT_PARENT_PREFIX)) return description;
    const rest = description.slice(EIF_DISTRICT_PARENT_PREFIX.length);
    const newline = rest.indexOf('\n');
    return newline === -1 ? '' : rest.slice(newline + 1);
}

export function buildInterimLocationDescription(
    districtId: string | null | undefined,
    userDescription?: string | null,
): string | null {
    const text = (userDescription || '').trim();
    if (!districtId) return text || null;
    return text ? `${EIF_DISTRICT_PARENT_PREFIX}${districtId}\n${text}` : `${EIF_DISTRICT_PARENT_PREFIX}${districtId}`;
}

export function isInterimDistrictLocation(location: WithDescription): boolean {
    return getInterimDistrictParentId(location) == null;
}
